// src/ThreeJs/Trees/TreeCopyPaste.js
import * as THREE from "three";
import { sceneManager } from "../SetUps/SceneManager";
import { TREES, VARS } from "../Global/variables";

let ghost = null;
let ghostActive = false;

export function isTreePastePreviewActive() {
  return ghostActive;
}

function detectShapeFromTree(tree) {
  const foliage = tree?.userData?.foliage;
  const t = foliage?.geometry?.type || "";
  return t.includes("Sphere") ? "circle" : "cone";
}

function serializeTree(tree) {
  const trunk = tree?.userData?.trunk;
  const foliage = tree?.userData?.foliage;
  if (!tree || !trunk || !foliage) return null;

  return {
    shape: detectShapeFromTree(tree),
    groupScale: { x: tree.scale.x, y: tree.scale.y, z: tree.scale.z },
    trunkScale: { x: trunk.scale.x, y: trunk.scale.y, z: trunk.scale.z },
    foliageScale: { x: foliage.scale.x, y: foliage.scale.y, z: foliage.scale.z },
  };
}

function createTreeFromClipboard(data) {
  const tree = new THREE.Group();
  tree.name = `InteractiveTree_${Date.now()}`;

  const trunkHeight = 1.2;

  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.1, trunkHeight, 8),
    new THREE.MeshStandardMaterial({ color: 0x6b4f2a })
  );
  trunk.position.y = trunkHeight / 2;

  let foliage;
  if (data.shape === "circle") {
    foliage = new THREE.Mesh(
      new THREE.SphereGeometry(0.9, 12, 12),
      new THREE.MeshStandardMaterial({ color: 0x2f7d32 })
    );
    foliage.position.y = trunkHeight + 0.9;
  } else {
    foliage = new THREE.Mesh(
      new THREE.ConeGeometry(0.9, 1.8, 10),
      new THREE.MeshStandardMaterial({ color: 0x2f7d32 })
    );
    foliage.position.y = trunkHeight + 0.9;
  }

  const arrow = new THREE.Mesh(
    new THREE.ConeGeometry(0.12, 0.3, 6),
    new THREE.MeshStandardMaterial({ color: 0xff0000 })
  );
  arrow.position.y = trunkHeight + 2.1;
  arrow.name = "heightArrow";
  arrow.visible = false;

  tree.add(trunk, foliage, arrow);

  tree.userData.trunk = trunk;
  tree.userData.foliage = foliage;
  tree.userData.arrow = arrow;

  tree.userData.__trunkBaseHeight = trunkHeight;
  tree.userData.__foliageOffset = 0.9;
  tree.userData.__arrowOffset = 2.1;

  tree.rotation.x = Math.PI / 2;

  tree.scale.set(data.groupScale.x, data.groupScale.y, data.groupScale.z);
  trunk.scale.set(data.trunkScale.x, data.trunkScale.y, data.trunkScale.z);
  foliage.scale.set(data.foliageScale.x, data.foliageScale.y, data.foliageScale.z);

  tree.position.z = 0;
  tree.userData.groundZ = 0;

  return tree;
}

function applyGhostStyle(obj) {
  obj.traverse((o) => {
    if (!o.isMesh) return;
    const m = o.material?.clone?.();
    if (!m) return;
    m.transparent = true;
    m.opacity = 0.35;
    m.depthWrite = false;
    m.wireframe = true;
    o.material = m;
  });
}

function disposeObject(obj) {
  if (!obj) return;
  obj.traverse((o) => {
    if (o.geometry) o.geometry.dispose?.();
    if (o.material) {
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
      else o.material.dispose?.();
    }
  });
}

function getPastePointFromMouse() {
  const scene = sceneManager.scene;
  const camera = sceneManager.camera;
  if (!scene || !camera) return null;

  const ndc = VARS.mouseNDC || { x: 0, y: 0 };
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, camera);

  const meshes = VARS.Meshes || [];
  if (meshes.length) {
    const hits = raycaster.intersectObjects(meshes, true);
    if (hits.length) {
      const p = hits[0].point.clone();
      p.z = 0;
      return p;
    }
  }

  const fallback = new THREE.Vector3(0, 0, 0);
  fallback.z = 0;
  return fallback;
}

function updateGhostPosition() {
  if (!ghostActive || !ghost) return;
  const p = getPastePointFromMouse();
  if (!p) return;
  ghost.position.copy(p);
  ghost.position.z = 0;
  ghost.userData.groundZ = 0;
}

function onMouseMoveUpdate() {
  updateGhostPosition();
}

function onCanvasClickPaste(e) {
  if (!ghostActive) return;
  const canvas = sceneManager?.renderer?.domElement;
  if (!canvas) return;

  const target = e.target;
  const clickedCanvas = target === canvas || (canvas.contains && canvas.contains(target));
  if (!clickedCanvas) return;
  if (e.button !== 0) return;

  e.preventDefault();
  pasteCopiedTree();
}

export function copySelectedTree(tree) {
  const data = serializeTree(tree);
  if (!data) return;

  VARS.copiedTree = data;
  console.log("📋 Copied tree:", data);

  startTreePastePreview();
}

export function startTreePastePreview() {
  const data = VARS.copiedTree;
  const scene = sceneManager.scene;
  if (!data || !scene) return;

  stopTreePastePreview();

  ghost = createTreeFromClipboard(data);
  ghost.name = `TreePasteGhost_${Date.now()}`;
  ghost.userData.__isPasteGhost = true;

  applyGhostStyle(ghost);

  scene.add(ghost);
  ghostActive = true;

  updateGhostPosition();

  window.addEventListener("mousemove", onMouseMoveUpdate);
  window.addEventListener("pointerdown", onCanvasClickPaste, true);
}

export function stopTreePastePreview() {
  ghostActive = false;

  window.removeEventListener("mousemove", onMouseMoveUpdate);
  window.removeEventListener("pointerdown", onCanvasClickPaste, true);

  if (ghost) {
    try {
      ghost.parent?.remove(ghost);
    } catch (e) {}
    disposeObject(ghost);
    ghost = null;
  }
}

export function pasteCopiedTree() {
  const data = VARS.copiedTree;
  const scene = sceneManager.scene;
  if (!data || !scene) return null;

  const p = getPastePointFromMouse();
  if (!p) return null;

  const tree = createTreeFromClipboard(data);
  tree.position.copy(p);
  tree.position.z = 0;
  tree.userData.groundZ = 0;

  scene.add(tree);
  TREES.trees.push(tree);

  // Select it (updates editor)
  VARS.selectedTree = tree;
  window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree } }));

  // Stop after one paste (prevents accidental spam)
  stopTreePastePreview();

  return tree;
}
